import { apiDownloadUrl, apiGet } from './api';

export type ComparisonMetric = {
  current: number;
  previous: number;
  changePct: number;
};

export type DashboardAlertItem = {
  type: string;
  count: number;
  severity: 'error' | 'warning' | 'info';
  label: string;
  link: string | null;
};

export type RegistrationFunnel = {
  registered: number;
  verified: number;
  firstLogin: number;
  firstDocumentViewed: number;
  trialStarted: number;
  paidSubscription: number;
};

export type TeacherUtilization = {
  average: number;
  max: number;
  overloaded: number;
  totalTeachers: number;
};

export type DashboardData = {
  periodDays: number;
  period?: { days: number; start: string; end: string; groupBy: string };
  refreshedAt?: string;
  users: {
    total: number;
    byRole: Array<{ role: string; count: number }>;
    byStatus: Array<{ status: string; count: number }>;
    signupsOverTime: Array<{ date: string; count: number }>;
    activeOverTime?: Array<{ date: string; count: number }>;
    active7d: number;
    active30d: number;
    activeInPeriod?: number;
    paidUsersInPeriod?: number;
    activeMetric?: string;
    registrationFunnel?: RegistrationFunnel;
  };
  teacherUtilization?: TeacherUtilization;
  dailyRollups?: Array<{ date: string; metrics: Record<string, number> }>;
  subscriptions: {
    active: number;
    trial: number;
    canceled: number;
    expired: number;
    grace_period: number;
    other: number;
  };
  subscriptionsV2?: {
    uniqueUsers: {
      activePaid: number;
      trial: number;
      grace_period: number;
      canceledOrExpired: number;
    };
    trialsExpiringSoon: Array<{
      subscriptionId: string;
      userId: string;
      email?: string;
      nickname?: string;
      planName?: string;
      trialEndDate?: string;
    }>;
  };
  revenue?: {
    inPeriod: number;
    grossInPeriod: number;
    currency: string;
    transactionCount: number;
    overTime: Array<{ date: string; amount: number }>;
    byCurrency?: Array<{ currency: string; netAmount: number; count: number }>;
  };
  mrr?: {
    current: number;
    currency: string;
    paidSubscribers: number;
  };
  comparison?: {
    signups: ComparisonMetric;
    sessionsInPeriod: ComparisonMetric;
    active30d: ComparisonMetric;
    active7d: ComparisonMetric;
    revenueInPeriod?: ComparisonMetric;
    mrr?: ComparisonMetric;
    activeInPeriod?: ComparisonMetric;
    paidUsersInPeriod?: ComparisonMetric;
  };
  sessions: {
    total: number;
    lastPeriod: number;
    completed: number;
    overTime: Array<{ date: string; count: number }>;
  };
  topDocuments: Array<{ documentCode: string; views: number; totalTimeMinutes: number }>;
  teacherWorkload: Array<{
    teacherId: string;
    email?: string;
    nickname?: string;
    studentCount: number;
  }>;
  alerts?: {
    trialsExpiringSoon: number;
    failedPayments24h: number;
    failedPayments7d: number;
    gracePeriodUsers: number;
    bannedUsers: number;
    bannedRecent: number;
    orphanedTrackingSessions: number;
    orphanedCourseSessions: number;
    items: DashboardAlertItem[];
  };
};

export type DashboardParams = {
  days?: number;
  compare?: boolean;
  groupBy?: 'day' | 'week' | 'month';
  preset?: 'today' | 'ytd' | 'scholar' | 'scholar_last' | 'custom';
  startDate?: string;
  endDate?: string;
};

export async function getAdminDashboard(params: DashboardParams = {}) {
  const { days = 30, compare = true, groupBy = 'day', preset, startDate, endDate } = params;
  const qs = new URLSearchParams({ groupBy });
  if (preset) qs.set('preset', preset);
  else if (startDate && endDate) {
    qs.set('startDate', startDate);
    qs.set('endDate', endDate);
  } else {
    qs.set('days', String(days));
  }
  if (compare) qs.set('compare', 'previous');
  return apiGet<{ success: boolean; data: DashboardData; cached?: boolean }>(
    `/api/v1/admin/dashboard?${qs.toString()}`
  );
}

export async function getAdminAlerts(days = 30) {
  return apiGet<{ success: boolean; data: DashboardData['alerts'] & { refreshedAt: string } }>(
    `/api/v1/admin/dashboard/alerts?days=${days}`
  );
}

export function getDashboardExportUrl(params: DashboardParams = {}): string {
  const { days = 30, groupBy = 'day', preset, startDate, endDate } = params;
  const qs = new URLSearchParams({
    groupBy,
    format: 'csv',
  });
  if (preset) qs.set('preset', preset);
  else if (startDate && endDate) {
    qs.set('startDate', startDate);
    qs.set('endDate', endDate);
  } else {
    qs.set('days', String(days));
  }
  return apiDownloadUrl(`/api/v1/admin/dashboard?${qs.toString()}`);
}

export async function downloadDashboardCsv(params: DashboardParams = {}) {
  const url = getDashboardExportUrl(params);
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Export failed (${res.status})`);
  }
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `dashboard-${params.preset || (params.days ? `${params.days}d` : 'export')}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function formatMoney(amount: number, currency = 'EUR') {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}
